"use client"

import { useEffect } from "react"

const EXCLUDED_DOMAINS = ["w3era.com", "wranker.com", "vefogix.com"]

export default function ExternalLinkHandler() {
  useEffect(() => {
    const handleExternalLinks = () => {
      // Get all anchor tags on the page
      const links = document.querySelectorAll("a[href]")

      links.forEach((link) => {
        const href = link.getAttribute("href")

        // Skip if no href or if it's an internal link
        if (
          !href ||
          href.startsWith("/") ||
          href.startsWith("#") ||
          href.startsWith("mailto:") ||
          href.startsWith("tel:")
        ) {
          return
        }

        try {
          const url = new URL(href, window.location.origin)
          const currentDomain = window.location.hostname
          const linkDomain = url.hostname

          // Check if it's an external link
          if (linkDomain !== currentDomain) {
            // Check if the domain should be excluded
            const shouldExclude = EXCLUDED_DOMAINS.some(
              (domain) => linkDomain === domain || linkDomain.endsWith(`.${domain}`),
            )

            if (!shouldExclude) {
              // Add nofollow and noreferrer
              const currentRel = link.getAttribute("rel") || ""
              const relValues = currentRel.split(" ").filter(Boolean)

              // Add nofollow if not present
              if (!relValues.includes("nofollow")) {
                relValues.push("nofollow")
              }

              // Add noreferrer if not present
              if (!relValues.includes("noreferrer")) {
                relValues.push("noreferrer")
              }

              link.setAttribute("rel", relValues.join(" "))
            }
          }
        } catch (error) {
          // Handle invalid URLs gracefully
          console.warn("Invalid URL found:", href)
        }
      })
    }

    // Run on initial load
    handleExternalLinks()

    // Set up a MutationObserver to handle dynamically added links
    const observer = new MutationObserver((mutations) => {
      let shouldReprocess = false

      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element
              // Check if the added node is a link or contains links
              if (element.tagName === "A" || element.querySelector("a")) {
                shouldReprocess = true
              }
            }
          })
        }
      })

      if (shouldReprocess) {
        handleExternalLinks()
      }
    })

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    // Cleanup
    return () => {
      observer.disconnect()
    }
  }, [])

  return null // This component doesn't render anything
}
